import React, { useEffect, useRef, useState } from 'react';
import { AiOutlineDollarCircle } from 'react-icons/ai';
import { TbLogout, TbUserCircle, TbBriefcase, TbArrowsExchange } from 'react-icons/tb';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { MdAccountBalanceWallet, MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';

import ClientAppsMenu from '@/components/ClientAppsMenu';
import MovideskChatButton from '@/components/MovideskChatButton';
import OctadeskChatButton from '@/components/OctadeskChatButton';
import { APP_COMMERCIAL_PANEL_BASE_URL, RV_GROUP_CLIENT_PANEL_BASE_URL } from '@/config';
import { useUser } from '@/contexts/UserContext';
import contactImage from '@/images/icons/header-contact.svg';
import { checkAccessTokenIsExpired } from '@/services/isTokenExpired';

import CircleRede from './svg-icons/circle-rede';

const HeaderMenu = ({ credits }) => {
  const [disabledByModality, setDisabledByModality] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonEl = useRef(null);

  const user = useUser();
  const navigate = useNavigate();

  const franchiseeContact = user?.franchiseeContact || null;
  const isFranchisee = user?.authRole === 'ROLE_FRANCHISEE';
  const isClient = user?.authRole === 'ROLE_CLIENT';
  const isClientWithSupportWidgetFeature = isClient && user?.features?.includes('CLIENT_SUPPORT_WIDGET');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && menuRef.current && !menuRef.current.contains(event.target) && !buttonEl.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    const meStr = window.sessionStorage.getItem('me');
    if (meStr) {
      try {
        const me = JSON.parse(meStr);
        setDisabledByModality(!!me?.disabledByModality);
      } catch (err) {
        console.debug('HeaderMenu JSON parse error', err);
      }
    }
  }, []);

  const integrations = () => {
    navigate('/marketplace');
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    window.sessionStorage.clear();
    checkAccessTokenIsExpired();
  };

  const handleDeleteOctadeskScript = () => {
    const octadeskElements = document.querySelectorAll('[class^="octa"]');

    if (octadeskElements.length > 0) {
      octadeskElements.forEach(octadeskElement => octadeskElement.remove());
    }
  };

  const handleRedirectToApp = (url, isUrlClient) => {
    const accessToken = sessionStorage.getItem('accessToken');
    const tokenType = sessionStorage.getItem('tokenType');
    const urlBase = isUrlClient ? RV_GROUP_CLIENT_PANEL_BASE_URL : APP_COMMERCIAL_PANEL_BASE_URL;
    window.open(`${urlBase}/${url}#access_token=${accessToken}&token_type=${tokenType}&expires_in=86400`, '_blank');
    setOpen(false);
  };

  useEffect(() => {
    if (!isClientWithSupportWidgetFeature) {
      handleDeleteOctadeskScript();
    }
  }, [isClientWithSupportWidgetFeature]);

  
  return (
    <div className="relative flex h-full w-full items-center justify-end" data-cy="header-menu">
      {franchiseeContact && (
        <a
          href={`https://wa.me/${franchiseeContact.value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-[0.5em] flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[#4c8fc9] font-bold text-white transition-colors first:ml-0 hover:bg-[#66a4d8]"
        >
          <img src={contactImage} alt="WhatsApp" className="max-h-[22px]" />
        </a>
      )}

      {isClientWithSupportWidgetFeature && <OctadeskChatButton />}
      {!isClient && <MovideskChatButton />}

      <div className="relative ml-[0.5em]">
        {isClient ? (
          <div
            ref={buttonEl}
            onClick={handleToggle}
            className="flex h-[36px] cursor-pointer items-center !rounded-[18px] border-0 bg-transparent py-0 pr-[4px] pl-[12px] text-white transition-[background-color] duration-300 ease-linear focus:outline-none"
            data-cy="logout-menu-button"
          >
            <div className="flex flex-col items-end px-[6px] pt-[10px] pb-[10px]">
              <div className="font-['Open_Sans'] text-[12px] font-bold leading-[1.2] text-[#dfe0e2] text-right">
                {user?.name}
              </div>
              <div className="mt-[4px] flex items-center font-['Open_Sans'] text-[14px] font-bold lowercase leading-[1.2] text-white">
                <MdAccountBalanceWallet size={16} className="mr-[6px] text-[#888f9c]" />
                {credits !== undefined ? credits : 0} <FormattedMessage id="Financial.credits" />
              </div>
            </div>
            <div className="flex items-center">
              {open ? (
                <MdArrowDropUp size={28} className="text-[#AAADB1]" />
              ) : (
                <MdArrowDropDown size={28} className="text-[#AAADB1]" />
              )}
            </div>
          </div>
        ) : (
          <button
            ref={buttonEl}
            type="button"
            onClick={handleToggle}
            className="flex h-[36px] items-center !rounded-[18px] border-0 bg-[#1f4774] py-0 pr-[4px] pl-[18px] text-white transition-[background-color] duration-300 ease-linear hover:bg-[#265182] focus:outline-none"
            data-cy="logout-menu-button"
          >
            <span className="font-['Open_Sans'] text-[12px] font-bold leading-[1.2] text-white">{user?.name}</span>
            <div className="ml-[0.2em] flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full leading-none">
              <CircleRede />
            </div>
          </button>
        )}

        {open && (
          <div
            ref={menuRef}
            className="absolute right-0 top-[40px] z-[99999999] w-[250px] origin-top-right rounded-[3px] border-none bg-white py-[4px] shadow-[0_2px_4px_rgba(0,0,0,0.2)]"
          >
            {(isFranchisee || isClient) && !disabledByModality && (
              <>
                <div 
                  onClick={() => handleRedirectToApp(isFranchisee ? 'franchisee-edit' : 'profile', isClient)} 
                  className="flex cursor-pointer select-none items-center px-4 h-10 text-[14px] text-[#333333] transition-colors hover:bg-[#f5f5f5] font-['Open_Sans']"
                >
                  <TbUserCircle size={22} className="mr-[10px] text-[#222222]" /> <FormattedMessage id="profile" />
                </div>
                <div 
                  onClick={() => handleRedirectToApp(isFranchisee ? 'clients' : 'company', isClient)} 
                  className="flex cursor-pointer select-none items-center px-4 h-10 text-[14px] text-[#333333] transition-colors hover:bg-[#f5f5f5] font-['Open_Sans']"
                >
                  <TbBriefcase size={22} className="mr-[10px] text-[#222222]" /> <FormattedMessage id="companies" />
                </div>
                {isFranchisee && (
                  <div 
                    onClick={() => handleRedirectToApp('open-orders', false)} 
                    className="flex cursor-pointer select-none items-center px-4 h-10 text-[14px] text-[#333333] transition-colors hover:bg-[#f5f5f5] font-['Open_Sans']"
                  >
                    <AiOutlineDollarCircle size={22} className="mr-[10px] text-[#222222]" /> <FormattedMessage id="sales" />
                  </div>
                )}
              </>
            )}

            {isClient && user?.features?.includes('INTEGRATION') && (
              <div 
                onClick={integrations} 
                className="flex cursor-pointer select-none items-center px-4 h-10 text-[14px] text-[#333333] transition-colors hover:bg-[#f5f5f5] font-['Open_Sans']"
              >
                <TbArrowsExchange size={22} className="mr-[10px] text-[#222222]" /> <FormattedMessage id="INTEGRATION" />
              </div>
            )}

            <div 
              onClick={handleLogout} 
              className="flex cursor-pointer select-none items-center px-4 h-10 text-[14px] text-[#333333] transition-colors hover:bg-[#f5f5f5] font-['Open_Sans']" 
              data-cy="logout-button"
            >
              <TbLogout size={22} className="mr-[10px] text-[#222222]" /> <FormattedMessage id="LOGOUT" />
            </div>
          </div>
        )}
      </div>

      {isClient && <ClientAppsMenu />}
    </div>
  );
};

HeaderMenu.propTypes = {
  credits: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default HeaderMenu;
